import React, { useEffect, useMemo, useState } from 'react';
import { AutocompleteProps, Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ItemService } from '../../services/item.service';
import { Item } from '../../interfaces/item.interface';
import SearchableSelect from '../common/SearchableSelect';

/** How many items to preload when the box is opened with an empty search. */
export const ITEM_DEFAULT_PAGE_SIZE = 10;
/** How many matches to fetch once the user has typed something. */
export const ITEM_SEARCH_PAGE_SIZE = 20;

const SEARCH_DEBOUNCE_MS = 300;

export const itemLabel = (option: Item): string => `${option.itemName} (${option.itemCode})`;

/**
 * Backs the item picker used by the BOM editor and consumption entry screens
 * (garments-merchandising T7 — 9.3/11.5). No shared item picker exists elsewhere in the app
 * (checked inventory/procurement screens — they use a plain MUI Select), so this mirrors
 * `BuyerAutocomplete.tsx`'s debounce-search shape against `ItemService.getItemsByInstitution`.
 */
const useItemSearch = (enabled: boolean) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<Item[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setOptions([]);
            return;
        }
        const search = inputValue.trim();
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const size = search ? ITEM_SEARCH_PAGE_SIZE : ITEM_DEFAULT_PAGE_SIZE;
                const res = await ItemService.getItemsByInstitution(0, size, search);
                if (!cancelled) setOptions(res.content || []);
            } catch (err) {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, search ? SEARCH_DEBOUNCE_MS : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [inputValue, enabled]);

    return { options, loading, inputValue, setInputValue };
};

const withSelected = (options: Item[], selected: Item | null): Item[] => {
    if (!selected) return options;
    return options.some(o => o.id === selected.id) ? options : [selected, ...options];
};

type PassthroughProps = Partial<Pick<AutocompleteProps<Item, false, false, false>, 'openOnFocus' | 'autoHighlight'>>;

interface ItemAutocompleteProps extends PassthroughProps {
    value: Item | null;
    onChange: (value: Item | null) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    'data-testid'?: string;
    /** Marks an option non-selectable (e.g. an item already in this style's BOM); shows "Already in BOM" beside it. */
    getOptionDisabled?: (option: Item) => boolean;
    disabledOptionText?: string;
}

export function ItemAutocomplete({
    value,
    onChange,
    label = 'Item',
    placeholder = 'Search items...',
    size = 'small',
    fullWidth = true,
    disabled = false,
    required,
    error,
    helperText,
    sx,
    getOptionDisabled,
    disabledOptionText = 'Already in BOM',
    ...rest
}: ItemAutocompleteProps) {
    const { options, loading, inputValue, setInputValue } = useItemSearch(!disabled);

    const mergedOptions = useMemo(() => withSelected(options, value), [options, value]);

    return (
        <SearchableSelect<Item>
            options={mergedOptions}
            value={value?.id ?? ''}
            selectedOption={value}
            onChange={(_id, option) => onChange(option)}
            getOptionLabel={itemLabel}
            getOptionDisabled={getOptionDisabled}
            // The server has already filtered; re-filtering here would hide matches.
            disableClientFilter
            onInputChange={setInputValue}
            loading={loading}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            sx={sx}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            // Let the option list grow past the (narrow) input so long item names are readable.
            slotProps={{ popper: { style: { width: 'fit-content', minWidth: 320, maxWidth: 'min(560px, 92vw)' } } }}
            noOptionsText={loading ? 'Loading…' : inputValue ? 'No items found' : 'No items'}
            renderOption={getOptionDisabled ? (props, option) => (
                <Box component="li" {...props} key={option.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                >
                    <span>{itemLabel(option)}</span>
                    {getOptionDisabled(option) && (
                        <Typography variant="caption" color="text.disabled">{disabledOptionText}</Typography>
                    )}
                </Box>
            ) : undefined}
            {...rest}
        />
    );
}

export default ItemAutocomplete;
